import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useEffect, useState } from 'react';
import DrawResult from '../pages/DrawResult/DrawResult';

export default function ProtectedRoute({ children, allowedRoles = [] }) {
  const { isAuthenticated, tokens, user } = useAuth();
  const location = useLocation();
  const [isTokenValid, setIsTokenValid] = useState(true);

  useEffect(() => {
    console.log(`ProtectedRoute check at ${location.pathname}:`, {
      isAuthenticated,
      hasUser: !!user,
      hasToken: !!tokens?.access?.token,
      tokenExpiry: tokens?.access?.expiresAt
        ? new Date(tokens.access.expiresAt).toISOString()
        : 'none',
    });

    // Check token validity but don't redirect immediately
    if (tokens?.access?.expiresAt) {
      const expiryDate = new Date(tokens.access.expiresAt);
      const now = new Date();
      setIsTokenValid(expiryDate > now);
    }
  }, [isAuthenticated, tokens, user, location.pathname]);

  if (location.pathname === '/game-results' && !isAuthenticated) {
    return <DrawResult />;
  }

  // Only check if authenticated (token validation will be handled by API calls)
  if (!isAuthenticated) {
    console.log(
      `Redirecting from ${location.pathname} to /login due to no authentication`
    );
    // Store the attempted URL for redirecting back after login
    sessionStorage.setItem('redirectAfterLogin', location.pathname);
    return <Navigate to='/login' replace />;
  }

  // Check role access if allowedRoles is specified
  if (allowedRoles.length > 0 && user) {
    const hasRequiredRole = allowedRoles.includes(user.roleId);

    if (!hasRequiredRole) {
      console.log(
        `User with role ${user.roleId} does not have access to ${location.pathname}`
      );
      return <Navigate to='/account' replace />;
    }
  }

  // Finance officers (role 10) can only access a limited set of pages.
  // The account page is kept as the navigation hub.
  const FINANCE_OFFICER_ROLE = 10;
  const financeOfficerAllowedPaths = [
    '/account',
    '/withdrawal-request',
    '/hot-numbers',
    '/reports',
  ];
  if (user && user.roleId === FINANCE_OFFICER_ROLE) {
    const isAllowed = financeOfficerAllowedPaths.some(
      (path) =>
        location.pathname === path ||
        location.pathname.startsWith(`${path}/`)
    );

    if (!isAllowed) {
      console.log(
        `Finance officer does not have access to ${location.pathname}`
      );
      return <Navigate to='/account' replace />;
    }
  }

  // If token is invalid, warn in console but don't redirect
  // Let actual API calls handle expired tokens
  if (!isTokenValid) {
    console.warn(
      'Token appears to be expired but continuing to render protected route'
    );
  }

  return children;
}
